import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Lock } from "lucide-react";
import { companyChipClasses, companyChipLabel } from "@/lib/companyColors";

const MARGEN_STYLES = {
  verde: "bg-green-50 text-green-700 border-green-200",
  amarillo: "bg-amber-50 text-amber-700 border-amber-200",
  rojo: "bg-red-50 text-red-700 border-red-200",
};

export function MargenBadge({ margen_pct, margen_color, testId }) {
  if (margen_pct === undefined || margen_pct === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span
      data-testid={testId}
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${MARGEN_STYLES[margen_color] || MARGEN_STYLES.rojo}`}
    >
      {margen_pct.toFixed(1)}%
    </span>
  );
}

export function EstadoRegistroBadge({ estado, testId }) {
  if (estado === "invalidado") {
    return <Badge data-testid={testId} className="border border-slate-300 bg-slate-200 text-slate-600 hover:bg-slate-200">Invalidado</Badge>;
  }
  return <Badge data-testid={testId} className="border border-green-200 bg-green-50 text-green-700 hover:bg-green-50">Activo</Badge>;
}

export function EstadoCobroBadge({ estado, dias, testId }) {
  const STYLES = {
    cobrado: "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50",
    critico: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-50",
    vencido: "border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-50",
    por_vencer: "border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50",
    pendiente: "border border-slate-300 bg-slate-100 text-slate-600 hover:bg-slate-100",
  };
  const LABELS = { cobrado: "Cobrado", critico: "Crítico", vencido: "Vencido", por_vencer: "Por vencer", pendiente: "Pendiente" };
  const key = estado || "pendiente";
  let diasLabel = "";
  if (dias != null && key !== "cobrado") {
    diasLabel = dias > 0 ? ` · ${dias}d vencido` : dias < 0 ? ` · vence en ${Math.abs(dias)}d` : " · vence hoy";
  }
  return (
    <Badge data-testid={testId} className={STYLES[key] || STYLES.pendiente}>
      {LABELS[key] || key}{diasLabel}
    </Badge>
  );
}

export function EstadoPagoBadge({ estado, testId }) {
  const STYLES = {
    pagado_total: "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50",
    parcial: "border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50",
    sin_pagos: "border border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-100",
    // legacy values kept for safety
    pagado: "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50",
    pendiente: "border border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-100",
  };
  const LABELS = { pagado_total: "Pagado total", parcial: "Parcial", sin_pagos: "Sin pagos", pagado: "Pagado total", pendiente: "Sin pagos" };
  const key = estado || "sin_pagos";
  return <Badge data-testid={testId} className={STYLES[key] || STYLES.sin_pagos}>{LABELS[key] || key}</Badge>;
}

export function FueraTarifaBadge({ testId }) {
  return (
    <Badge data-testid={testId} className="border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-50 gap-1">
      <AlertTriangle className="h-3 w-3" /> Fuera de tarifa
    </Badge>
  );
}

export function BloqueadoBadge({ testId }) {
  return (
    <Badge data-testid={testId} className="border border-slate-400 bg-slate-100 text-slate-700 hover:bg-slate-100 gap-1">
      <Lock className="h-3 w-3" /> Bloqueado
    </Badge>
  );
}

// Chip de color consistente por compañía (Parte D) — Álvarez azul, Trébol turquesa.
export function CompanyChip({ numeroLoad, testId }) {
  return (
    <Badge data-testid={testId} className={`${companyChipClasses(numeroLoad)} hover:bg-inherit`}>
      {companyChipLabel(numeroLoad)}
    </Badge>
  );
}

export const TIPO_VIAJE_LABELS = {
  nacional_mx: "Nacional MX",
  nacional_us: "Nacional US",
  transfronterizo: "Transfronterizo",
};

export function TipoViajeBadge({ tipo, testId }) {
  const isXB = tipo === "transfronterizo";
  return (
    <Badge
      data-testid={testId}
      className={isXB ? "border border-primary/30 bg-primary/10 text-primary hover:bg-primary/10" : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-50"}
    >
      {TIPO_VIAJE_LABELS[tipo] || tipo}
    </Badge>
  );
}

export const COST_TYPE_LABELS = {
  carrier_mx: "Carrier MX",
  carrier_us: "Carrier US",
  cruce: "Cruce",
  papeleria: "Papelería",
  demoras: "Demoras",
  otro: "Otro",
  diesel: "Diesel",
  casetas: "Casetas",
  maniobras: "Maniobras",
  viaticos_operador: "Viáticos del operador",
  otros_operativos: "Otros operativos",
};

// Tipos de costo operativo de flota propia (nacionales MX) — nunca exigen carrier.
export const COST_TYPES_SIN_CARRIER = ["cruce", "papeleria", "demoras", "otro", "diesel", "casetas",
  "maniobras", "viaticos_operador", "otros_operativos"];
